import { useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { heapEvent } from '@equipmentshare/heaper'
import PermissionsCheck from 'components/PermissionsCheck'
import { Permission } from '../../../graphql'
import { StorePart } from '../../../types/storePart'
import ItemActionsMenu from './components/ItemActionsMenu'
import StatePanel from './components/StatePanel'
import MovementHistorySection from './movement-history/MovementHistorySection'

export type ItemDetailRedesignProps = {
	item: StorePart,
	/** Whether the Transaction History panel is enabled — resolved by the page container,
	 * which also has to know so it can invalidate the `ItemHistory` query after an action. */
	showTransactionHistory: boolean,
	onEdit: () => void,
	onAdjustQuantity: () => void,
	onConvertItems: () => void,
	onConvertItemsToMany: () => void,
	onOverrideWac: () => void,
	onDeleteItem: () => void,
}

/** A quiet muted-label + value pair for header metadata. */
function HeaderMeta( { label, value }: { label: string, value: string } ) {
	return (
		<Stack direction="row" alignItems="baseline" gap={0.75}>
			<Typography variant="body2" color="text.secondary">{label}</Typography>
			<Typography variant="body2">{value}</Typography>
		</Stack>
	)
}

/**
 * MUI layout for the item detail page (behind `costentory-enable-item-details-v2`).
 * Full-width shell — header, current-state panel, and the new Movement History section.
 * Modeled on the shipped `itemMasters/detail` page (EUI page chrome + MUI content).
 */
export default function ItemDetailRedesign( props: ItemDetailRedesignProps ) {
	const { item, showTransactionHistory, onEdit, onAdjustQuantity, onConvertItems, onConvertItemsToMany, onOverrideWac, onDeleteItem } = props

	useEffect( () => {
		heapEvent( 'Inventory - Item Details - View - New Layout', {} )
	}, [] )

	const manufacturer = item.part.provider.name
	const description = item.part.partClass.description

	return (
		<Box sx={{ p: 3 }}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
				<Box>
					<Link component={RouterLink} to="/" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 14 }}>
						<ChevronLeftIcon fontSize="small" />
						Inventory
					</Link>
					<Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mt={0.5}>
						<Box>
							<Stack direction="row" alignItems="baseline" flexWrap="wrap" columnGap={1.5} rowGap={0.5}>
								<Typography variant="h4" component="h1">Part #: {item.part.partNumber}</Typography>
								<Typography variant="h6" component="span" color="text.secondary" fontWeight={400}>Description: {description}</Typography>
							</Stack>
							<Stack direction="row" flexWrap="wrap" columnGap={3} rowGap={0.5} mt={1.5}>
								<HeaderMeta label="Manufacturer" value={manufacturer} />
								<HeaderMeta label="Part ID" value={String( item.part.id )} />
								{item.location && <HeaderMeta label="Location" value={item.location} />}
							</Stack>
						</Box>
						<PermissionsCheck permissionsToCheck={[ Permission.CanEditStorePartsInfoByStoreId ]}>
							<ItemActionsMenu
								onEdit={onEdit}
								onAdjustQuantity={onAdjustQuantity}
								onConvertItems={onConvertItems}
								onConvertItemsToMany={onConvertItemsToMany}
								onOverrideWac={onOverrideWac}
								onDeleteItem={onDeleteItem}
							/>
						</PermissionsCheck>
					</Stack>
				</Box>

				<StatePanel item={item} />

				{showTransactionHistory && <MovementHistorySection storePart={item} />}
			</Box>
		</Box>
	)
}
